import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { APP_LOGO_URL } from "../../config/branding";
import NotificationBell from "../../components/NotificationBell";
import {
  FaHome,
  FaUser,
  FaCut,
  FaCalendarAlt,
  FaBook,
  FaDollarSign,
  FaBoxOpen,
  FaBell,
  FaCog,
  FaBars,
  FaSignOutAlt,
  FaExchangeAlt,
  FaClipboardList,
  FaGift,
  FaWallet,
  FaImages,
} from "react-icons/fa";
import axios from "axios";
import LogoutConfirmModal from "../../components/LogoutConfirmModal";
import "../shops/shopStyles.css"; // Reuse existing styles
import "./staffStyles.css"; // Staff-specific styles including bottom-nav fix

function StaffSidebarLayout({ children, roleType = "professional" }) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [isFreelancer, setIsFreelancer] = useState(false);
  const [isR2, setIsR2] = useState(false);
  const [productSalesEnabled, setProductSalesEnabled] = useState(null); // null = loading, true/false = loaded
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  const SIDEBAR_COLLAPSE_KEY = `ogestyle_sidebar_collapsed_staff_${roleType}`;

  useEffect(() => {
    const token = localStorage.getItem("ogestyle_token");
    setIsLoggedIn(!!token);

    // Check if freelancer from localStorage
    const userStr = localStorage.getItem("ogestyle_user");
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        setIsFreelancer(user.userType === "freelancer");
      } catch (e) {}
    }

    // R2 is a shop admin, NOT a package professional (Phase 4 / D17): an R2 cannot subscribe to a
    // package or deliver its sessions, so this menu must not be offered to them. `/dashboard/professional`
    // carries no role guard, so an R2 CAN reach this layout — hiding the item is not redundant.
    // The same localStorage keys the shop sidebar reads, including the override a multi-role user sets.
    const currentRole =
      localStorage.getItem("ogestyle_role_override") ||
      localStorage.getItem("ogestyle_role");
    setIsR2(currentRole === "R2");

    // Fetch business settings to check if product sales are enabled
    const fetchBusinessSettings = async () => {
      try {
        const API_BASE_URL =
          process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";

        // Get staff profile to find businessId
        const staffRes = await axios.get(`${API_BASE_URL}/api/staff/profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const staffData = staffRes.data.staff || staffRes.data;
        const busId =
          typeof staffData.businessId === "object"
            ? staffData.businessId?._id
            : staffData.businessId;

        if (busId) {
          const businessRes = await axios.get(
            `${API_BASE_URL}/api/businesses/${busId}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            },
          );

          const productSettings = businessRes.data.productSettings || {
            allowProfessionalProductSales: true,
          };
          setProductSalesEnabled(productSettings.allowProfessionalProductSales);
          setSettingsLoaded(true);
        } else {
          // No business ID, default to false
          setProductSalesEnabled(false);
          setSettingsLoaded(true);
        }
      } catch (error) {
        // Default to disabled if we can't fetch settings (safer)
        setProductSalesEnabled(false);
        setSettingsLoaded(true);
      }
    };

    if (token) {
      fetchBusinessSettings();
    } else {
      setSettingsLoaded(true);
    }

    const savedCollapsed = localStorage.getItem(SIDEBAR_COLLAPSE_KEY);
    if (savedCollapsed === "1") {
      setCollapsed(true);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(SIDEBAR_COLLAPSE_KEY, collapsed ? "1" : "0");
  }, [collapsed, SIDEBAR_COLLAPSE_KEY]);

  const handleLogout = () => {
    localStorage.removeItem("ogestyle_token");
    localStorage.removeItem("ogestyle_role");
    localStorage.removeItem("ogestyle_roles");
    localStorage.removeItem("ogestyle_role_override");
    setIsLoggedIn(false);
    window.location.href = "/login";
  };

  // Determine basePath based on roleType prop
  const basePath = `/dashboard/${roleType}`;

  /**
   * THE PACKAGES LINK IS THE ONE EXCEPTION TO `basePath` (found doing Phase 14, 2026-10-21).
   *
   * Every other entry is `${basePath}/…` because each role has its own copy of the page. This one does not:
   * the only routed screen is `/dashboard/professional/package-subscriptions`, and `PackageSubscriptions` fixes
   * its own `roleType` to "professional" rather than reading it from the URL. So on a booth-renter page the old
   * `${basePath}/package-subscriptions` resolved to `/dashboard/booth-renter/package-subscriptions`, which has no
   * route — a menu entry that went nowhere. The path is named once here so the desktop and mobile copies cannot
   * drift apart again.
   */
  const packageSubscriptionsPath = "/dashboard/professional/package-subscriptions";

  // The roles this session holds — the same source the server authorises from.
  const rolesList = JSON.parse(localStorage.getItem("ogestyle_roles") || "[]");

  /**
   * WHO MAY SUBSCRIBE TO A SHOP'S PACKAGE: R3 ONLY (user, 2026-10-21: *"booth renters R7 are not supposed to
   * offer/see packages"*).
   *
   * The server already says so — `isEligiblePackageProfessional` admits `role: "R3"` and nothing else, because
   * **R4 and R7 cannot deliver a business's package sessions**: letting them subscribe would promise a customer
   * a package that will not be delivered, and quote the professional an earning they can never be paid.
   *
   * So this is not a policy made here — it is the menu agreeing with the gate. R4 was already excluded; R7 was
   * not, and its entry had merely pointed at a route that does not exist for it. A visible entry that always ends
   * in a refusal is worse than no entry: it reads as a broken feature rather than as a rule.
   *
   * THE ROLE CODE, NOT `roleType`. Most staff pages hardcode `roleType = "professional"` (only the payouts and
   * looks pages receive "booth-renter" from the route), so keying this on the prop alone would hide the entry on
   * an R7's payouts page and show it again on their schedule page. `ogestyle_roles` is the same source the server
   * authorises from.
   */
  const holdsProfessionalRole =
    rolesList.includes("R3") || rolesList.includes("professional");
  const isBoothRenterOnly =
    (rolesList.includes("R7") || roleType === "booth-renter") &&
    !holdsProfessionalRole;
  const canSeePackageSubscriptions =
    !isFreelancer && !isR2 && !isBoothRenterOnly;

  // Check if user has business account access (owner/admin roles)
  const hasDualOwnerProfessionalRoles =
    rolesList.includes("R1") && rolesList.includes("R3");
  const hideProfessionalPayouts =
    roleType === "professional" && hasDualOwnerProfessionalRoles;
  const canOwner = rolesList.includes("owner") || rolesList.includes("R1");
  const canAdmin =
    rolesList.includes("shop_admin") ||
    rolesList.includes("R2") ||
    rolesList.includes("business_admin");
  const canSwitchToBusiness = canOwner || canAdmin;

  const handleSwitchClick = async (e) => {
    e.preventDefault();
    try {
      const targetRoleCode = canOwner ? "R1" : "R2";
      const token = localStorage.getItem("ogestyle_token");
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/switch-role`,
        { targetRoleCode },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const { token: newToken, role } = res.data;
      localStorage.setItem("ogestyle_token", newToken);
      localStorage.setItem("ogestyle_role", role);
      localStorage.setItem("ogestyle_role_override", role);

      window.location.href = "/dashboard";
    } catch (err) {
      alert(err?.response?.data?.message || "Unable to switch account.");
    }
  };

  return (
    <div className="dashboard-layout theme-typography">
      {/* Sidebar for Desktop */}
      <aside
        className={`dashboard-sidebar${collapsed ? " collapsed" : ""}${mobileMenuOpen ? " mobile-open" : ""}`}
      >
        <div
          className="sidebar-logo"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            paddingRight: collapsed ? "0" : "12px",
          }}
        >
          <img
            className="sidebar-logo-image"
            src={APP_LOGO_URL}
            alt="OGESTYLE Logo"
          />
          {!collapsed && (
            <NotificationBell
              viewAllPath={
                roleType === "freelancer" || isFreelancer
                  ? "/dashboard/freelancer/notifications"
                  : "/dashboard/professional/notifications"
              }
            />
          )}
          <button
            className="sidebar-toggle"
            onClick={() => setCollapsed((prev) => !prev)}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            <FaBars />
          </button>
        </div>

        <ul
          onClick={(event) => {
            if (window.innerWidth <= 900 && event.target.closest("a")) {
              setMobileMenuOpen(false);
            }
          }}
        >
          <li className="menu-item">
            <Link to={basePath}>
              <span className="menu-icon">
                <FaHome />
              </span>
              <span className="menu-text">Dashboard</span>
            </Link>
          </li>
          <li className="menu-item">
            <Link to={`${basePath}/profile`}>
              <span className="menu-icon">
                <FaUser />
              </span>
              <span className="menu-text">Profile</span>
            </Link>
          </li>
          <li className="menu-item">
            <Link to={`${basePath}/services`}>
              <span className="menu-icon">
                <FaCut />
              </span>
              <span className="menu-text">Services</span>
            </Link>
          </li>
          {/* Temporarily hidden - Professional customers page */}
          {/* <li className="menu-item">
            <Link to={`${basePath}/customers`}>
              <span className="menu-icon">👥</span>
              <span className="menu-text">Customers</span>
            </Link>
          </li> */}
          <li className="menu-item">
            <Link to={`${basePath}/schedule`}>
              <span className="menu-icon">
                <FaCalendarAlt />
              </span>
              <span className="menu-text">Calendar Management</span>
            </Link>
          </li>
          <li className="menu-item">
            <Link to={`${basePath}/bookings`}>
              <span className="menu-icon">
                <FaBook />
              </span>
              <span className="menu-text">Bookings</span>
            </Link>
          </li>
          <li className="menu-item">
            <Link to={`${basePath}/looks`}>
              <span className="menu-icon">
                <FaImages />
              </span>
              <span className="menu-text">My Looks</span>
            </Link>
          </li>
          {/* Products - Only show if business allows professional product sales */}
          {settingsLoaded && productSalesEnabled && (
            <li className="menu-item">
              <Link to={`${basePath}/products`}>
                <span className="menu-icon">
                  <FaBoxOpen />
                </span>
                <span className="menu-text">Products</span>
              </Link>
            </li>
          )}
          {/* Service products handed to this professional. NOT gated on product sales:
              this is consumable stock the business issues for appointments, which is a
              different thing from selling retail products, and it is the professional's
              own record of what they are holding. */}
          <li className="menu-item">
            <Link to={`${basePath}/inventory`}>
              <span className="menu-icon">🧴</span>
              <span className="menu-text">My Service Products</span>
            </Link>
          </li>
          {settingsLoaded && productSalesEnabled && (
            <li className="menu-item">
              <Link to={`${basePath}/orders`}>
                <span className="menu-icon">
                  <FaBoxOpen />
                </span>
                <span className="menu-text">Orders</span>
              </Link>
            </li>
          )}
          {!hideProfessionalPayouts && (
            <li className="menu-item">
              <Link to={`${basePath}/payouts`}>
                <span className="menu-icon">
                  <FaWallet />
                </span>
                <span className="menu-text">Payouts</span>
              </Link>
            </li>
          )}
          <li className="menu-item">
            <Link to={`${basePath}/earnings`}>
              <span className="menu-icon">
                <FaDollarSign />
              </span>
              <span className="menu-text">Performance</span>
            </Link>
          </li>
          {/* RELEASED (Phase 14, 2026-10-21). This entry used to carry a "TEMPORARILY ENABLED FOR LOCAL REVIEW
              — do NOT commit or push to demo" guard, written when the professional's tranche was genuinely
              unimplemented. It is implemented now: one tranche per COMPLETED package session, from the
              purchase's frozen rate and the service delivered. The guard is gone because leaving it would
              tell the next reader the delivering professional is paid nothing, which is no longer true. */}
          {/* Hidden from R2 as well as R4 (Phase 4 / D17). */}
          {canSeePackageSubscriptions && (
            <li className="menu-item">
              <Link to={packageSubscriptionsPath}>
                <span className="menu-icon">
                  <FaGift />
                </span>
                <span className="menu-text">Package Subscriptions</span>
              </Link>
            </li>
          )}
          <li className="menu-item">
            <Link to={`${basePath}/waitlist`}>
              <span className="menu-icon">
                <FaClipboardList />
              </span>
              <span className="menu-text">Waitlist</span>
            </Link>
          </li>
          <li className="menu-item">
            <Link to={`${basePath}/reviews`}>
              <span className="menu-icon">⭐</span>
              <span className="menu-text">Reviews</span>
            </Link>
          </li>
          <li className="menu-item">
            <Link to={`${basePath}/notifications`}>
              <span className="menu-icon">
                <FaBell />
              </span>
              <span className="menu-text">Notifications</span>
            </Link>
          </li>
          <li className="menu-item">
            <Link to={`${basePath}/settings`}>
              <span className="menu-icon">
                <FaCog />
              </span>
              <span className="menu-text">Settings</span>
            </Link>
          </li>

          {/* Switch to Business Account */}
          {canSwitchToBusiness && (
            <li className="menu-item">
              <Link to="#" onClick={handleSwitchClick}>
                <span className="menu-icon">
                  <FaExchangeAlt />
                </span>
                <span className="menu-text">Business Account</span>
              </Link>
            </li>
          )}
        </ul>

        <button
          className="logout-button"
          onClick={() =>
            isLoggedIn
              ? setShowLogoutModal(true)
              : (window.location.href = "/login")
          }
        >
          <span className="menu-icon">
            <FaSignOutAlt />
          </span>
          <span className="menu-text">{isLoggedIn ? "Logout" : "Login"}</span>
        </button>
      </aside>

      {mobileMenuOpen && (
        <div
          className="mobile-sidebar-backdrop"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      <div className="mobile-sidebar-topbar">
        <button
          className="mobile-sidebar-toggle"
          onClick={() => setMobileMenuOpen((prev) => !prev)}
          aria-label="Open menu"
        >
          <FaBars />
        </button>
      </div>

      {/* Main Content */}
      <main className="dashboard-main">{children}</main>

      {/* Bottom Navigation for Mobile */}
      <nav className="bottom-nav">
        <Link to={basePath}>
          <span className="nav-icon">
            <FaHome />
          </span>
          <span>Home</span>
        </Link>
        <Link to={`${basePath}/bookings`}>
          <span className="nav-icon">
            <FaBook />
          </span>
          <span>Bookings</span>
        </Link>
        <Link to={`${basePath}/services`}>
          <span className="nav-icon">
            <FaCut />
          </span>
          <span>Services</span>
        </Link>
        <Link to={`${basePath}/looks`}>
          <span className="nav-icon">
            <FaImages />
          </span>
          <span>Looks</span>
        </Link>

        {/* More Menu Button */}
        <button
          onClick={() => setShowMoreMenu(!showMoreMenu)}
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            color: "#00509d",
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: "4px 8px",
            fontSize: "0.75rem",
            fontWeight: "486",
            minWidth: "70px",
            position: "relative",
          }}
        >
          <span className="nav-icon">
            <FaBars />
          </span>
          <span>More</span>
        </button>
      </nav>

      {/* More Menu Overlay */}
      {showMoreMenu && (
        <>
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0, 0, 0, 0.5)",
              zIndex: 998,
            }}
            onClick={() => setShowMoreMenu(false)}
          />
          <div
            style={{
              position: "fixed",
              bottom: "60px",
              left: 0,
              right: 0,
              background: "white",
              borderTop: "1px solid #e5e7eb",
              borderTopLeftRadius: "12px",
              borderTopRightRadius: "12px",
              boxShadow: "0 -4px 12px rgba(0, 0, 0, 0.15)",
              zIndex: 999,
              maxHeight: "60vh",
              overflowY: "auto",
            }}
          >
            <div style={{ padding: "16px" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "16px",
                  paddingBottom: "12px",
                  borderBottom: "2px solid #e5e7eb",
                }}
              >
                <h3 style={{ margin: 0, fontSize: "18px", fontWeight: "600" }}>
                  Menu
                </h3>
                <button
                  onClick={() => setShowMoreMenu(false)}
                  style={{
                    background: "none",
                    border: "none",
                    fontSize: "24px",
                    cursor: "pointer",
                    color: "#6b7280",
                    padding: "0",
                    width: "32px",
                    height: "32px",
                  }}
                >
                  ×
                </button>
              </div>

              <Link
                to={`${basePath}/profile`}
                onClick={() => setShowMoreMenu(false)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  padding: "12px",
                  color: "#374151",
                  textDecoration: "none",
                  borderRadius: "8px",
                  marginBottom: "4px",
                  fontSize: "16px",
                }}
              >
                <span style={{ fontSize: "20px", color: "#00509d" }}>
                  <FaUser />
                </span>
                <span>Profile</span>
              </Link>

              {/* Temporarily hidden - Professional customers page */}
              {/* <Link
                to={`${basePath}/customers`}
                onClick={() => setShowMoreMenu(false)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  padding: "12px",
                  color: "#374151",
                  textDecoration: "none",
                  borderRadius: "8px",
                  marginBottom: "4px",
                  fontSize: "16px",
                }}
              >
                <span style={{ fontSize: "20px" }}>👥</span>
                <span>Customers</span>
              </Link> */}

              {/* Products - Only show if business allows professional product sales */}
              {settingsLoaded && productSalesEnabled && (
                <Link
                  to={`${basePath}/products`}
                  onClick={() => setShowMoreMenu(false)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    padding: "12px",
                    color: "#374151",
                    textDecoration: "none",
                    borderRadius: "8px",
                    marginBottom: "4px",
                    fontSize: "16px",
                  }}
                >
                  <span style={{ fontSize: "20px", color: "#00509d" }}>
                    <FaBoxOpen />
                  </span>
                  <span>Products</span>
                </Link>
              )}

              {/* Orders - Only show if business allows professional product sales */}
              {settingsLoaded && productSalesEnabled && (
                <Link
                  to={`${basePath}/orders`}
                  onClick={() => setShowMoreMenu(false)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    padding: "12px",
                    color: "#374151",
                    textDecoration: "none",
                    borderRadius: "8px",
                    marginBottom: "4px",
                    fontSize: "16px",
                  }}
                >
                  <span style={{ fontSize: "20px" }}>🛍️</span>
                  <span>Orders</span>
                </Link>
              )}

              <Link
                to={`${basePath}/looks`}
                onClick={() => setShowMoreMenu(false)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  padding: "12px",
                  color: "#374151",
                  textDecoration: "none",
                  borderRadius: "8px",
                  marginBottom: "4px",
                  fontSize: "16px",
                }}
              >
                <span style={{ fontSize: "20px", color: "#00509d" }}>
                  <FaImages />
                </span>
                <span>My Looks</span>
              </Link>

              {!hideProfessionalPayouts && (
                <Link
                  to={`${basePath}/payouts`}
                  onClick={() => setShowMoreMenu(false)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    padding: "12px",
                    color: "#374151",
                    textDecoration: "none",
                    borderRadius: "8px",
                    marginBottom: "4px",
                    fontSize: "16px",
                  }}
                >
                  <span style={{ fontSize: "20px", color: "#00509d" }}>
                    <FaWallet />
                  </span>
                  <span>Payouts</span>
                </Link>
              )}

              <Link
                to={`${basePath}/earnings`}
                onClick={() => setShowMoreMenu(false)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  padding: "12px",
                  color: "#374151",
                  textDecoration: "none",
                  borderRadius: "8px",
                  marginBottom: "4px",
                  fontSize: "16px",
                }}
              >
                <span style={{ fontSize: "20px", color: "#00509d" }}>
                  <FaDollarSign />
                </span>
                <span>Performance</span>
              </Link>

              {/* Mobile copy of the released entry above — same state, same gate. */}
              {/* The desktop entry above is gated to match; this one was ungated, so an R4 freelancer
                  could still reach it from the mobile overflow menu. */}
              {canSeePackageSubscriptions && (
                <Link
                  to={packageSubscriptionsPath}
                  onClick={() => setShowMoreMenu(false)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    padding: "12px",
                    color: "#374151",
                    textDecoration: "none",
                    borderRadius: "8px",
                    marginBottom: "4px",
                    fontSize: "16px",
                  }}
                >
                  <span style={{ fontSize: "20px", color: "#00509d" }}>
                    <FaGift />
                  </span>
                  <span>Package Subscriptions</span>
                </Link>
              )}

              <Link
                to={`${basePath}/waitlist`}
                onClick={() => setShowMoreMenu(false)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  padding: "12px",
                  color: "#374151",
                  textDecoration: "none",
                  borderRadius: "8px",
                  marginBottom: "4px",
                  fontSize: "16px",
                }}
              >
                <span style={{ fontSize: "20px", color: "#00509d" }}>
                  <FaClipboardList />
                </span>
                <span>Waitlist</span>
              </Link>

              <Link
                to={`${basePath}/reviews`}
                onClick={() => setShowMoreMenu(false)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  padding: "12px",
                  color: "#374151",
                  textDecoration: "none",
                  borderRadius: "8px",
                  marginBottom: "4px",
                  fontSize: "16px",
                }}
              >
                <span style={{ fontSize: "20px" }}>⭐</span>
                <span>Reviews</span>
              </Link>

              <Link
                to={`${basePath}/notifications`}
                onClick={() => setShowMoreMenu(false)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  padding: "12px",
                  color: "#374151",
                  textDecoration: "none",
                  borderRadius: "8px",
                  marginBottom: "4px",
                  fontSize: "16px",
                }}
              >
                <span style={{ fontSize: "20px", color: "#00509d" }}>
                  <FaBell />
                </span>
                <span>Notifications</span>
              </Link>

              <Link
                to={`${basePath}/settings`}
                onClick={() => setShowMoreMenu(false)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  padding: "12px",
                  color: "#374151",
                  textDecoration: "none",
                  borderRadius: "8px",
                  marginBottom: "4px",
                  fontSize: "16px",
                }}
              >
                <span style={{ fontSize: "20px", color: "#00509d" }}>
                  <FaCog />
                </span>
                <span>Settings</span>
              </Link>

              {canSwitchToBusiness && (
                <Link
                  to="#"
                  onClick={(e) => {
                    handleSwitchClick(e);
                    setShowMoreMenu(false);
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    padding: "12px",
                    color: "#374151",
                    textDecoration: "none",
                    borderRadius: "8px",
                    marginBottom: "4px",
                    fontSize: "16px",
                    borderTop: "1px solid #e5e7eb",
                    marginTop: "8px",
                    paddingTop: "16px",
                  }}
                >
                  <span style={{ fontSize: "20px", color: "#00509d" }}>
                    <FaExchangeAlt />
                  </span>
                  <span>Switch to Business</span>
                </Link>
              )}

              <button
                onClick={() => {
                  setShowMoreMenu(false);
                  if (isLoggedIn) {
                    setShowLogoutModal(true);
                  } else {
                    window.location.href = "/login";
                  }
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  padding: "12px",
                  color: "#dc2626",
                  textDecoration: "none",
                  borderRadius: "8px",
                  width: "100%",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  fontSize: "16px",
                  fontWeight: "500",
                  borderTop: "1px solid #e5e7eb",
                  marginTop: "8px",
                  paddingTop: "16px",
                  textAlign: "left",
                }}
              >
                <span style={{ fontSize: "20px" }}>
                  <FaSignOutAlt />
                </span>
                <span>{isLoggedIn ? "Logout" : "Login"}</span>
              </button>
            </div>
          </div>
        </>
      )}

      <LogoutConfirmModal
        isOpen={showLogoutModal}
        onCancel={() => setShowLogoutModal(false)}
        onConfirm={handleLogout}
      />
    </div>
  );
}

export default StaffSidebarLayout;
